import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Button,
  Spinner,
  Select,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";

const { BASE_URL, ENDPOINTS: { ROLE_BASED_ACCESS } } = API_CONFIG;

const PAGE_SIZE = 10;

/* ─── Exclude these mainMenu groups ─── */
const EXCLUDED_MENUS = ["Inventory"];

const excludeMenus = (items) =>
  items.filter(
    (item) => !EXCLUDED_MENUS.map((m) => m.toLowerCase()).includes(item.mainMenu?.toLowerCase())
  );

export default function RoleBasedAccess() {
  const toast = useToast();

  /* ─── State ─── */
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState("");

  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);

  const [checkedIds, setCheckedIds] = useState(new Set());
  const [submitLoading, setSubmitLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  /* ─── Fetch Roles on Mount ─── */
  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await apiClient.get(`${BASE_URL}${ROLE_BASED_ACCESS.ROLES}`);
      setRoles(res.data);
    } catch (err) {
      console.error("Error fetching roles:", err);
      toast({
        title: "Error",
        description: "Failed to fetch roles. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRolesLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  /* ─── Auto-fetch menu items when dropdown changes ─── */
  const fetchMenuItems = useCallback(async (roleId) => {
    if (!roleId) {
      setMenuItems([]);
      setCheckedIds(new Set());
      setCurrentPage(1);
      return;
    }
    setMenuLoading(true);
    setMenuItems([]);
    setCheckedIds(new Set());
    setCurrentPage(1);
    try {
      const res = await apiClient.get(
        `${BASE_URL}${ROLE_BASED_ACCESS.ROLE_MENU}/${roleId}`
      );
      const filtered = excludeMenus(res.data);
      setMenuItems(filtered);
      const preChecked = new Set(
        filtered
          .filter((item) => item.permissionStatus?.toUpperCase() === "ASSIGNED")
          .map((item) => item.submenuId)
      );
      setCheckedIds(preChecked);
    } catch (err) {
      console.error("Error fetching menu items:", err);
      toast({
        title: "Error",
        description: "Failed to fetch menu permissions. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setMenuLoading(false);
    }
  }, [toast]);

  const handleRoleChange = (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
    fetchMenuItems(roleId);
  };

  /* ─── Save Permissions ─── */
  const handleSubmit = async () => {
    const payload = Array.from(checkedIds).map((submenuId) => ({
      roleId: Number(selectedRoleId),
      submenuId,
    }));
    setSubmitLoading(true);
    try {
      await apiClient.post(
        `${BASE_URL}${ROLE_BASED_ACCESS.ROLE_MENU}/${selectedRoleId}`,
        payload
      );
      toast({
        title: "Permissions Updated",
        description: "Role menu permissions have been saved successfully.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Submit error:", err);
      toast({
        title: "Error",
        description: "Failed to save permissions. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  /* ─── Checkbox toggle ─── */
  const handleCheckboxChange = (submenuId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(submenuId) ? next.delete(submenuId) : next.add(submenuId);
      return next;
    });
  };

  /* ─── Select All (across all pages) ─── */
  const allIds = menuItems.map((i) => i.submenuId);
  const allChecked = allIds.length > 0 && allIds.every((id) => checkedIds.has(id));
  const isIndeterminate = !allChecked && allIds.some((id) => checkedIds.has(id));
  const handleSelectAll = () => {
    setCheckedIds(allChecked ? new Set() : new Set(allIds));
  };

  /* ─── Pagination ─── */
  const totalPages = Math.ceil(menuItems.length / PAGE_SIZE);
  const paginatedItems = menuItems.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  /* ─── Group paginated items by mainMenu ─── */
  const groupedItems = paginatedItems.reduce((acc, item) => {
    if (!acc[item.mainMenu]) acc[item.mainMenu] = [];
    acc[item.mainMenu].push(item);
    return acc;
  }, {});

  /* ─── Derive selected role name ─── */
  const selectedRoleName =
    roles.find((r) => String(r.roleId) === String(selectedRoleId))?.roleName || "";

  return (
    <Box p={{ base: 4, md: 6 }} mt={10} minH="100vh" bg="gray.50">

      {/* ══════════════════════════════════════
          HEADER CARD — title + dropdown only
      ══════════════════════════════════════ */}
      <Box
        bg="white"
        borderRadius="12px"
        boxShadow="0 2px 8px rgba(0,0,0,0.07)"
        mb={5}
        overflow="hidden"
      >
        {/* Red title bar */}
        {/* <Box
          bgGradient="linear(to-r, #C72127, #e53e3e)"
          px={6}
          py={4}
        >
          <Heading color="white" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
            Role Based Access
          </Heading>
          <Text color="red.100" fontSize="sm" mt={0.5}>
            Manage menu permissions for each role
          </Text>
        </Box> */}

        {/* Dropdown row */}
        <Flex px={6} py={5} align="flex-end" gap={4} flexWrap="wrap">
          <Box minW={{ base: "100%", sm: "260px" }} maxW="340px">
            <Text fontSize="13px" fontWeight="600" mb={1.5} color="gray.600">
              Select Role
            </Text>
            {rolesLoading ? (
              <Flex align="center" gap={2} h="40px">
                <Spinner size="sm" color="red.500" />
                <Text fontSize="sm" color="gray.400">Loading roles…</Text>
              </Flex>
            ) : (
              <Select
                placeholder="— Choose a role —"
                value={selectedRoleId}
                onChange={handleRoleChange}
                borderColor="gray.300"
                focusBorderColor="#C72127"
                borderRadius="8px"
                bg="white"
                fontSize="14px"
                h="40px"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  borderColor: "#C72127",
                  boxShadow: "0 0 0 3px rgba(199,33,39,0.15)",
                }}
              >
                {roles.map((role) => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName}
                  </option>
                ))}
              </Select>
            )}
          </Box>
        </Flex>
      </Box>

      {/* ══════════════════════════════════════
          LOADING SPINNER
      ══════════════════════════════════════ */}
      {menuLoading && (
        <Flex justify="center" align="center" h="220px">
          <Spinner size="xl" color="red.500" thickness="4px" />
        </Flex>
      )}

      {/* ══════════════════════════════════════
          PERMISSIONS TABLE CARD
      ══════════════════════════════════════ */}
      {!menuLoading && menuItems.length > 0 && (
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 2px 8px rgba(0,0,0,0.07)"
          overflow="hidden"
        >
          {/* Table title bar */}
          <Flex
            bg="#C72127"
            px={6}
            py={3}
            align="center"
            justify="space-between"
          >
            <Text color="white" fontWeight="700" fontSize="14px">
              Menu Permissions{selectedRoleName ? ` — ${selectedRoleName}` : ""}
            </Text>
            <Text color="red.100" fontSize="13px">
              {checkedIds.size} / {menuItems.length} selected
            </Text>
          </Flex>

          {/* Table */}
          <Box overflowX="auto">
            <Table variant="simple" size="md">
              <Thead>
                <Tr bg="red.50">
                  <Th
                    w="56px"
                    borderBottom="2px solid"
                    borderColor="red.200"
                    py={3}
                  >
                    <Checkbox
                      isChecked={allChecked}
                      isIndeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      colorScheme="red"
                    />
                  </Th>
                  {["Main Menu", "Sub Menu", "Status"].map((col) => (
                    <Th
                      key={col}
                      color="gray.600"
                      fontSize="12px"
                      fontWeight="700"
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      borderBottom="2px solid"
                      borderColor="red.200"
                      py={3}
                    >
                      {col}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {Object.entries(groupedItems).map(([mainMenu, items], groupIdx) =>
                  items.map((item, itemIdx) => (
                    <Tr
                      key={item.submenuId}
                      bg={groupIdx % 2 === 0 ? "white" : "gray.50"}
                      _hover={{ bg: "red.50" }}
                      transition="background 0.12s"
                    >
                      {/* Checkbox */}
                      <Td borderColor="gray.100" py={3} pl={6}>
                        <Checkbox
                          isChecked={checkedIds.has(item.submenuId)}
                          onChange={() => handleCheckboxChange(item.submenuId)}
                          colorScheme="red"
                        />
                      </Td>

                      {/* Main Menu — show label only on first row of group */}
                      <Td borderColor="gray.100" py={3}>
                        {itemIdx === 0 ? (
                          <Text
                            fontSize="14px"
                            fontWeight="600"
                            color="gray.800"
                          >
                            {mainMenu}
                          </Text>
                        ) : (
                          <Box w="100%" h="1px" />
                        )}
                      </Td>

                      {/* Sub Menu */}
                      <Td borderColor="gray.100" py={3}>
                        <Text fontSize="14px" color="gray.700">
                          {item.subMenuName}
                        </Text>
                      </Td>

                      {/* Status badge */}
                      <Td borderColor="gray.100" py={3}>
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          px={3}
                          py={1}
                          borderRadius="full"
                          fontSize="11px"
                          fontWeight="700"
                          letterSpacing="0.03em"
                          bg={
                            item.permissionStatus?.toUpperCase() === "ASSIGNED"
                              ? "green.50"
                              : "red.50"
                          }
                          color={
                            item.permissionStatus?.toUpperCase() === "ASSIGNED"
                              ? "green.600"
                              : "red.600"
                          }
                          border="1px solid"
                          borderColor={
                            item.permissionStatus?.toUpperCase() === "ASSIGNED"
                              ? "green.200"
                              : "red.200"
                          }
                        >
                          {item.permissionStatus}
                        </Box>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>

          {/* ── Pagination + Save row ── */}
          <Flex
            px={6}
            py={4}
            borderTop="1px solid"
            borderColor="gray.100"
            align="center"
            justify="space-between"
            flexWrap="wrap"
            gap={3}
          >
            {/* Pagination */}
            <Flex align="center" gap={2}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                leftIcon={<ChevronLeftIcon />}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                isDisabled={currentPage === 1}
                borderRadius="8px"
                h="34px"
                fontSize="13px"
                _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                _hover={{ borderColor: "gray.400" }}
              >
                Prev
              </Button>

              <Flex align="center" gap={1}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Box
                    key={page}
                    as="button"
                    onClick={() => setCurrentPage(page)}
                    w="32px"
                    h="32px"
                    borderRadius="8px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="13px"
                    fontWeight={currentPage === page ? "700" : "500"}
                    bg={currentPage === page ? "#C72127" : "transparent"}
                    color={currentPage === page ? "white" : "gray.600"}
                    border="1px solid"
                    borderColor={currentPage === page ? "#C72127" : "gray.200"}
                    transition="all 0.15s"
                    _hover={{
                      bg: currentPage === page ? "#a81b20" : "red.50",
                      borderColor: "#C72127",
                      color: currentPage === page ? "white" : "#C72127",
                    }}
                  >
                    {page}
                  </Box>
                ))}
              </Flex>

              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                rightIcon={<ChevronRightIcon />}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                isDisabled={currentPage === totalPages}
                borderRadius="8px"
                h="34px"
                fontSize="13px"
                _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                _hover={{ borderColor: "gray.400" }}
              >
                Next
              </Button>

              <Text fontSize="13px" color="gray.400" ml={1}>
                {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, menuItems.length)} of{" "}
                {menuItems.length}
              </Text>
            </Flex>

            {/* Save button */}
            <Button
              bg="#C72127"
              color="white"
              h="38px"
              px={8}
              fontSize="14px"
              fontWeight="700"
              borderRadius="8px"
              boxShadow="0 2px 6px rgba(199,33,39,0.3)"
              _hover={{
                bg: "#a81b20",
                transform: submitLoading ? "none" : "translateY(-1px)",
                boxShadow: "0 4px 10px rgba(199,33,39,0.4)",
              }}
              _active={{ bg: "#8e151a", transform: "none" }}
              transition="all 0.2s"
              isLoading={submitLoading}
              loadingText="Saving…"
              spinner={<Spinner size="sm" color="white" />}
              onClick={handleSubmit}
            >
              Save Permissions
            </Button>
          </Flex>
        </Box>
      )}

      {/* ══════════════════════════════════════
          EMPTY STATE
      ══════════════════════════════════════ */}
      {!menuLoading && menuItems.length === 0 && selectedRoleId && (
        <Flex
          bg="white"
          borderRadius="12px"
          boxShadow="0 2px 8px rgba(0,0,0,0.07)"
          justify="center"
          align="center"
          direction="column"
          gap={2}
          py={16}
        >
          <Text fontSize="36px">🔒</Text>
          <Text fontWeight="700" color="gray.700" fontSize="16px">
            No menu items found
          </Text>
          <Text color="gray.400" fontSize="13px">
            No permissions available for the selected role.
          </Text>
        </Flex>
      )}
    </Box>
  );
}